/**
 * Public, user-owned MCP connections ("Connect AI tools"). These never grant
 * service/agent authority. Available on EVERY plan (Free included): an external
 * assistant runs on the user's own AI subscription, so it costs Projoint nothing.
 * Effective rights = token scopes ∩ the user's live team role, on every call.
 */
export const MCP_CONNECTION_OPTIONS = [
  // Creators may fill in what they just created (checklist items at create time).
  { id: 'create', label: 'Create tasks and subtasks', scopes: ['task.create', 'task.edit_as_creator'] },
  {
    id: 'edit',
    label: 'Edit tasks and checklists',
    scopes: [
      'task.edit_any',
      'task.edit_as_creator',
      'task.edit_as_respondent',
      'task.edit_as_assignee',
    ],
  },
  {
    id: 'status',
    label: 'Change task status',
    scopes: [
      'task.change_status_any',
      'task.change_status_as_assignee',
      'task.change_status_as_respondent',
      'task.change_status_to_complete',
    ],
  },
  { id: 'assign', label: 'Assign tasks', scopes: ['task.assign'] },
  { id: 'comment', label: 'Comment on tasks', scopes: ['task.comment'] },
  { id: 'time', label: 'Log your time', scopes: ['time.log_own'] },
  { id: 'docs', label: 'Write docs', scopes: ['page.edit'] },
  { id: 'chat', label: 'Post in team chat', scopes: ['channel.post'] },
  {
    id: 'projects',
    label: 'Create projects and milestones',
    scopes: ['project.create', 'project.manage_workflow'],
  },
  { id: 'project', label: 'Edit project settings', scopes: ['project.edit_any'] },
  { id: 'team', label: 'Edit team settings', scopes: ['team.edit'] },
] as const;

export type McpConnectionOption = (typeof MCP_CONNECTION_OPTIONS)[number]['id'];

/**
 * Everyday work an assistant needs to be useful; pre-ticked in the connect
 * form and on the OAuth consent page. The rest (chat, creating projects,
 * settings) is opt-in.
 */
export const MCP_RECOMMENDED_OPTIONS: readonly McpConnectionOption[] = [
  'create',
  'edit',
  'status',
  'assign',
  'comment',
  'time',
  'docs',
];

/**
 * Read access every connection carries: teams, projects, tasks, comments, docs,
 * people (to assign / mention) and the user's OWN My Work plan.
 */
export const MCP_READ_SCOPES = [
  'mcp.user',
  'mcp.read',
  'team.view',
  'task.view',
  'member.view',
  'me.plan:read',
] as const;

export function mcpConnectionScopes(selected: readonly McpConnectionOption[]): string[] {
  const options = MCP_CONNECTION_OPTIONS.filter((option) => selected.includes(option.id));
  return [
    ...new Set([
      ...MCP_READ_SCOPES,
      ...(options.length ? ['mcp.write'] : []),
      ...options.flatMap((option) => [...option.scopes]),
    ]),
  ];
}

/**
 * A PAT carrying `mcp.user` is bound to the user MCP endpoint (`/api/mcp/user`)
 * only — the credential route policy refuses it everywhere else, even with
 * `api.*` added. Such tokens are free on every plan; REST API tokens (scripts)
 * still need the paid `public_api` feature.
 */
export function isMcpOnlyScopeSet(scopes: readonly string[]): boolean {
  return scopes.includes('mcp.user');
}
